import * as React from 'react';
import {
  addSurface,
  buildSurface,
  makeOverlayContext,
  OverlayContextProvider,
  useOverlay,
} from './OverlayContext';

type Callback = (arg1: HTMLDivElement) => void;

type InternalProps = {
  isSurface: boolean;
  top: number;
  left: number;
  width: number;
  height: number;
  style?: React.CSSProperties;
  children: React.ReactNode;
};

const InternalOverlay = (props: InternalProps) => {
  const context = useOverlay();
  const overlay = React.useRef<HTMLDivElement>(null);
  const queue = React.useRef<Array<Callback> | null>(null);

  const createLayer = React.useCallback(() => {
    const overlayRef = overlay.current;
    if (!overlayRef) {
      return null;
    }

    const layer = overlayRef.ownerDocument.createElement('div');
    layer.setAttribute('data-automation-id', 'overlay');
    overlayRef.appendChild(layer);
    return layer;
  }, []);

  const addLayer = React.useCallback(
    (cb: Callback) => {
      const overlayRef = overlay.current;
      if (overlayRef) {
        const layer = createLayer();
        if (layer) {
          cb(layer);
        }
      } else {
        if (!queue.current) {
          queue.current = [];
        }
        queue.current.push(cb);
      }
    },
    [createLayer]
  );

  const removeLayer = React.useCallback((layer: HTMLDivElement) => {
    layer.remove();
  }, []);

  React.useEffect(() => {
    if (queue.current) {
      queue.current.forEach((cb) => {
        const layer = createLayer();
        if (layer) {
          cb(layer);
        }
      });
      queue.current = null;
    }
  }, [createLayer]);

  const {isSurface, top, left, width, height, style, children} = props;
  /* Constructs a Box object with top, left, width, height, x, and y properties.
   * Note: The Box object differs from the HTML Rect object in the inclusion of x and y properties.
   * These x and y properties are used to position overlay components in useFloatingBase().
   * So in useFloatingBase(), we type assert the Box object as a Rect object.
   */
  const box = React.useMemo(
    () => ({
      top,
      left,
      width,
      height,
      x: left,
      y: top,
    }),
    [top, left, width, height]
  );
  const surface = buildSurface(addLayer)(removeLayer)(box)(overlay);

  const nextContext = isSurface
    ? addSurface(surface)(context)
    : makeOverlayContext(surface);

  return (
    <OverlayContextProvider value={nextContext}>
      {children}
      <div
        ref={overlay}
        style={Object.assign(
          {
            position: 'absolute',
            top,
            left,
            width: '100%',
          },
          style
        )}
      />
    </OverlayContextProvider>
  );
};

type Props = {
  top: number;
  left: number;
  width: number;
  height: number;
  style?: React.CSSProperties;
  children: React.ReactNode;
};

export const Overlay = ({top, left, width, height, style, children}: Props) => (
  <InternalOverlay
    isSurface={false}
    top={top}
    left={left}
    width={width}
    height={height}
    style={style}
  >
    {children}
  </InternalOverlay>
);

export const Surface = ({top, left, width, height, style, children}: Props) => (
  <InternalOverlay
    isSurface
    top={top}
    left={left}
    width={width}
    height={height}
    style={style}
  >
    {children}
  </InternalOverlay>
);
